import React, {useState, useEffect, useMemo} from 'react';
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FolderIcon from '@mui/icons-material/Folder';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
// import MoreVertIcon from '@mui/icons-material/MoreVert';
import {useNavigate, useParams} from 'react-router-dom';
import {useQuery, useMutation} from '@apollo/client';
import {GET_GUIDE_EDIT_DATA, GET_DOCUMENT_GROUP, GET_ORGANIZATION_BRAND_PROFILES} from '../graphql/queries';
import {SAVE_GUIDE_DRAFT, REGENERATE_GUIDE_DRAFT, PUBLISH_GUIDE_DRAFT, REGENERATE_GUIDE_FROM_GROUP, SET_GUIDE_THEME} from '../graphql/mutations';
import {useAuth} from '../contexts/AuthContext';
import {auth} from '../config/firebase';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {useFileUpload} from '../hooks/useFileUpload';
import AttachmentPreview from '../components/AttachmentPreview';
import {colors} from '../theme/colors';
import {buildGuideUrl} from '../utils/urls';

// ============================================================================
// Guide content preview (reused for draft & published)
// ============================================================================

function GuideContentPreview({output}: {output: any}) {
  if (!output) return null;
  return (
    <Box>
      <Typography sx={{fontSize: 20, fontWeight: 600, mb: 0.5}}>{output.title}</Typography>
      <Typography sx={{fontSize: 14, color: '#6B7280', mb: 2, lineHeight: 1.5}}>
        {output.description}
      </Typography>

      {output.hero && (
        <Typography sx={{fontSize: 14, color: '#374151', mb: 2, fontStyle: 'italic'}}>
          {output.hero}
        </Typography>
      )}

      {output.steps?.length > 0 && (
        <Box sx={{mb: 2}}>
          <Typography sx={{fontSize: 13, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1}}>
            Steps ({output.steps.length})
          </Typography>
          {output.steps
            .slice()
            .sort((a: any, b: any) => a.order - b.order)
            .map((step: any) => (
              <Box key={step.id} sx={{py: 1, borderBottom: '1px solid #F3F4F6', '&:last-child': {borderBottom: 'none'}}}>
                <Typography sx={{fontSize: 14, fontWeight: 500}}>
                  {step.order}. {step.title}
                </Typography>
                {step.summary && (
                  <Typography sx={{fontSize: 13, color: '#6B7280', mt: 0.25}}>{step.summary}</Typography>
                )}
              </Box>
            ))}
        </Box>
      )}

      {output.deadlines?.length > 0 && (
        <Box sx={{mb: 2}}>
          <Typography sx={{fontSize: 13, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1}}>
            Deadlines
          </Typography>
          {output.deadlines.map((d: any, i: number) => (
            <Typography key={i} sx={{fontSize: 14, mb: 0.5}}>
              <strong>{d.label}:</strong> {d.date}
              {d.description ? ` — ${d.description}` : ''}
            </Typography>
          ))}
        </Box>
      )}

      {output.faqBlocks?.length > 0 && (
        <Box>
          <Typography sx={{fontSize: 13, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1}}>
            FAQs ({output.faqBlocks.length})
          </Typography>
          {output.faqBlocks.map((faq: any, i: number) => (
            <Box key={i} sx={{mb: 1.5}}>
              <Typography sx={{fontSize: 14, fontWeight: 500}}>Q: {faq.question}</Typography>
              <Typography sx={{fontSize: 14, color: '#6B7280'}}>A: {faq.answer}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function InheritedFileIcon({mimeType}: {mimeType: string}) {
  if (mimeType === 'application/pdf') {
    return <PictureAsPdfIcon sx={{fontSize: 20, color: colors.red[600]}} />;
  }
  if (mimeType.includes('spreadsheet') || mimeType.includes('csv') || mimeType.includes('excel')) {
    return <TableChartIcon sx={{fontSize: 20, color: colors.green[600]}} />;
  }
  return <InsertDriveFileIcon sx={{fontSize: 20, color: colors.blue[500]}} />;
}

function formatRelativeTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

// ============================================================================
// Main page
// ============================================================================

export default function EditGuidePage() {
  useDocumentTitle('Guides', 'Edit Guide');

  const {user} = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const {guideId} = useParams<{guideId: string}>();

  const [prompt, setPrompt] = useState('');
  const [promptInitialized, setPromptInitialized] = useState(false);
  const [previewMode, setPreviewMode] = useState<'draft' | 'published'>('draft');
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({open: false, message: '', severity: 'info'});
  const [themeMode, setThemeMode] = useState<'ORG_DEFAULT' | 'IMPORT_PROFILE' | 'GUIDE_OVERRIDE'>('ORG_DEFAULT');
  const [themeProfileId, setThemeProfileId] = useState('');
  const [themeOverrides, setThemeOverrides] = useState<{
    organizationDisplayName: string;
    logoUrl: string;
    headerLogoUrl: string;
    primaryColor: string;
    secondaryColor: string;
    tone: 'FORMAL' | 'FRIENDLY' | 'NEUTRAL';
  }>({
    organizationDisplayName: '',
    logoUrl: '',
    headerLogoUrl: '',
    primaryColor: '',
    secondaryColor: '',
    tone: 'NEUTRAL',
  });
  const [themeDirty, setThemeDirty] = useState(false);

  const fileUpload = useFileUpload();

  const getAuthToken = async () => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('Failed to get authentication token');
    return token;
  };

  const {data, loading: queryLoading, refetch, startPolling, stopPolling} = useQuery(GET_GUIDE_EDIT_DATA, {
    variables: {guideId},
    skip: !guideId || !user,
    fetchPolicy: 'network-only',
  });

  const editData = data?.guideEditData;
  const publishedVersion = editData?.publishedVersion;
  const draftVersion = editData?.draftVersion;
  const publishedOutput = publishedVersion?.generatedOutput;
  const draftOutput = draftVersion?.generatedOutput;

  // Source group data (when guide was generated from a document group)
  const sourceGroupId = editData?.sourceGroupId;
  const isOutOfDate = editData?.isOutOfDate;

  const {data: sourceGroupData} = useQuery(GET_DOCUMENT_GROUP, {
    variables: {id: sourceGroupId},
    skip: !sourceGroupId,
  });
  const sourceGroup = sourceGroupData?.documentGroup;
  const {data: brandProfilesData} = useQuery(GET_ORGANIZATION_BRAND_PROFILES, {
    variables: {orgId: editData?.orgId, includeArchived: false},
    skip: !editData || !user,
  });
  const brandProfiles = brandProfilesData?.organizationBrandProfiles || [];

  const [regenerateFromGroup, {loading: regeneratingFromGroup}] = useMutation(REGENERATE_GUIDE_FROM_GROUP);

  // Pre-seed prompt from draft or published
  useEffect(() => {
    if (!editData || promptInitialized) return;
    const seedPrompt = draftVersion?.generationPrompt || publishedVersion?.generationPrompt || '';
    setPrompt(seedPrompt);
    setPromptInitialized(true);
  }, [editData, draftVersion, publishedVersion, promptInitialized]);

  useEffect(() => {
    if (!editData) return;
    const theme = editData.theme;
    const resolvedTheme = editData.resolvedTheme;
    setThemeMode(theme?.brandMode || 'ORG_DEFAULT');
    setThemeProfileId(theme?.brandProfileId || '');
    setThemeOverrides({
      organizationDisplayName:
        theme?.overrides?.organizationDisplayName
        || resolvedTheme?.organizationDisplayName
        || '',
      logoUrl: theme?.overrides?.logoUrl || resolvedTheme?.logoUrl || '',
      headerLogoUrl: theme?.overrides?.headerLogoUrl || resolvedTheme?.headerLogoUrl || '',
      primaryColor: theme?.overrides?.primaryColor || resolvedTheme?.primaryColor || '',
      secondaryColor: theme?.overrides?.secondaryColor || resolvedTheme?.secondaryColor || '',
      tone: theme?.overrides?.tone || resolvedTheme?.tone || 'NEUTRAL',
    });
    setThemeDirty(false);
  }, [editData]);

  const [saveGuideDraft, {loading: saving}] = useMutation(SAVE_GUIDE_DRAFT);
  const [regenerateGuideDraft, {loading: regenerating}] = useMutation(REGENERATE_GUIDE_DRAFT);
  const [publishGuideDraft, {loading: publishing}] = useMutation(PUBLISH_GUIDE_DRAFT);
  const [setGuideThemeMutation, {loading: savingTheme}] = useMutation(SET_GUIDE_THEME);

  // ---------------------------------------------------------------------------
  // Dirty / diff state
  // ---------------------------------------------------------------------------

  const savedPrompt = draftVersion?.generationPrompt || publishedVersion?.generationPrompt || '';
  const isDirty = prompt.trim() !== savedPrompt.trim() || fileUpload.attachments.length > 0;

  const isGenerating = draftVersion?.generationStatus === 'GENERATING' || regenerating;
  const isCompleted = draftVersion?.generationStatus === 'COMPLETED' && !!draftOutput;

  // Poll while generation is running in the background worker
  useEffect(() => {
    if (isGenerating) {
      startPolling(3000);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [isGenerating, startPolling, stopPolling]);

  const draftDiffersFromPublished = useMemo(() => {
    if (!draftOutput) return false;
    if (!publishedOutput) return true; // no published version, draft is new
    // Shallow compare key fields
    if (draftOutput.title !== publishedOutput.title) return true;
    if (draftOutput.description !== publishedOutput.description) return true;
    if ((draftOutput.steps?.length || 0) !== (publishedOutput.steps?.length || 0)) return true;
    // Compare step IDs/titles
    const draftStepKeys = (draftOutput.steps || []).map((s: any) => `${s.order}:${s.title}`).join('|');
    const pubStepKeys = (publishedOutput.steps || []).map((s: any) => `${s.order}:${s.title}`).join('|');
    if (draftStepKeys !== pubStepKeys) return true;
    return false;
  }, [draftOutput, publishedOutput]);

  const draftStepCount = draftOutput?.steps?.length || 0;
  const hasTitle = !!draftOutput?.title;

  // Publish enable/disable logic
  const publishDisabledReason = useMemo(() => {
    if (isDirty) return 'Save draft first';
    if (!isCompleted) return 'Generate a draft first';
    if (!hasTitle || draftStepCount === 0) return 'Add at least 1 step to publish';
    if (!draftDiffersFromPublished) return 'No changes to publish';
    return null;
  }, [isDirty, isCompleted, hasTitle, draftStepCount, draftDiffersFromPublished]);

  // Save draft enable/disable
  const saveDraftDisabledReason = useMemo(() => {
    if (!prompt.trim()) return 'Add a prompt to save';
    if (!isDirty) return 'No changes to save';
    return null;
  }, [prompt, isDirty]);

  // Regenerate enable/disable
  const regenerateDisabledReason = useMemo(() => {
    if (!prompt.trim()) return 'Add a prompt to regenerate';
    if (isGenerating) return 'Generating...';
    return null;
  }, [prompt, isGenerating]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSaveDraft = async () => {
    if (!prompt.trim()) return;
    try {
      const token = await getAuthToken();
      await saveGuideDraft({
        variables: {
          guideId,
          prompt: prompt.trim(),
          attachmentIds: fileUpload.readyTempUploadIds.length > 0 ? fileUpload.readyTempUploadIds : undefined,
        },
        context: {headers: {authorization: `Bearer ${token}`}},
      });
      setSnackbar({open: true, message: 'Draft saved', severity: 'success'});
      refetch();
    } catch (err: any) {
      setSnackbar({open: true, message: err.message || 'Failed to save draft', severity: 'error'});
    }
  };

  const handleRegenerate = async () => {
    // If no draft exists yet, save first then regenerate
    if (!draftVersion) {
      await handleSaveDraft();
      return;
    }
    try {
      const token = await getAuthToken();
      setSnackbar({open: true, message: 'Regeneration started...', severity: 'info'});
      await regenerateGuideDraft({
        variables: {guideId, versionId: draftVersion.id},
        context: {headers: {authorization: `Bearer ${token}`}},
      });
      refetch();
    } catch (err: any) {
      setSnackbar({open: true, message: err.message || 'Regeneration failed', severity: 'error'});
      refetch();
    }
  };

  const handlePublish = async () => {
    if (!draftVersion) return;
    try {
      const token = await getAuthToken();
      await publishGuideDraft({
        variables: {guideId, versionId: draftVersion.id},
        context: {headers: {authorization: `Bearer ${token}`}},
      });
      setPublishDialogOpen(false);
      setPublishSuccess(true);
      refetch();
    } catch (err: any) {
      setPublishDialogOpen(false);
      setSnackbar({open: true, message: err.message || 'Failed to publish', severity: 'error'});
    }
  };

  const handleRegenerateFromGroup = async () => {
    if (!guideId) return;
    try {
      setSnackbar({open: true, message: 'Regenerating from sources...', severity: 'info'});
      await regenerateFromGroup({variables: {guideId}});
      setSnackbar({open: true, message: 'Guide updated from sources', severity: 'success'});
      refetch();
    } catch (err: any) {
      setSnackbar({open: true, message: err.message || 'Failed to regenerate from sources', severity: 'error'});
    }
  };

  const handleSaveTheme = async () => {
    if (!guideId) return;
    if (themeMode === 'IMPORT_PROFILE' && !themeProfileId) {
      setSnackbar({open: true, message: 'Select a brand profile before saving', severity: 'error'});
      return;
    }

    const resolvedTheme = editData?.resolvedTheme;
    const overrides: Record<string, string> = {};
    const trimmedDisplayName = themeOverrides.organizationDisplayName.trim();
    const trimmedLogoUrl = themeOverrides.logoUrl.trim();
    const trimmedHeaderLogoUrl = themeOverrides.headerLogoUrl.trim();
    const trimmedPrimaryColor = themeOverrides.primaryColor.trim();
    const trimmedSecondaryColor = themeOverrides.secondaryColor.trim();

    if (trimmedDisplayName && trimmedDisplayName !== (resolvedTheme?.organizationDisplayName || '').trim()) {
      overrides.organizationDisplayName = trimmedDisplayName;
    }
    if (trimmedLogoUrl && trimmedLogoUrl !== (resolvedTheme?.logoUrl || '').trim()) {
      overrides.logoUrl = trimmedLogoUrl;
    }
    if (trimmedHeaderLogoUrl && trimmedHeaderLogoUrl !== (resolvedTheme?.headerLogoUrl || '').trim()) {
      overrides.headerLogoUrl = trimmedHeaderLogoUrl;
    }
    if (trimmedPrimaryColor && trimmedPrimaryColor !== (resolvedTheme?.primaryColor || '').trim()) {
      overrides.primaryColor = trimmedPrimaryColor;
    }
    if (trimmedSecondaryColor && trimmedSecondaryColor !== (resolvedTheme?.secondaryColor || '').trim()) {
      overrides.secondaryColor = trimmedSecondaryColor;
    }
    if (themeOverrides.tone && themeOverrides.tone !== resolvedTheme?.tone) {
      overrides.tone = themeOverrides.tone;
    }

    try {
      const token = await getAuthToken();
      await setGuideThemeMutation({
        variables: {
          guideId,
          input: {
            brandMode: themeMode,
            ...(themeMode === 'IMPORT_PROFILE' && themeProfileId ? {brandProfileId: themeProfileId} : {}),
            ...(Object.keys(overrides).length > 0 ? {overrides} : {}),
          },
        },
        context: {headers: {authorization: `Bearer ${token}`}},
      });
      setThemeDirty(false);
      setSnackbar({open: true, message: 'Guide branding updated', severity: 'success'});
      refetch();
    } catch (err: any) {
      setSnackbar({open: true, message: err.message || 'Failed to update guide branding', severity: 'error'});
    }
  };

  const guidePublicUrl = editData ? buildGuideUrl(editData.orgSlug, editData.slug) : '';
  const guideFullUrl = editData ? `${window.location.origin}/#${guidePublicUrl}` : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(guideFullUrl);
    setSnackbar({open: true, message: 'Link copied', severity: 'success'});
  };

  // ---------------------------------------------------------------------------
  // Loading / empty states
  // ---------------------------------------------------------------------------

  if (queryLoading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
        <CircularProgress />
      </Box>
    );
  }

  if (!editData) {
    return (
      <Box sx={{py: 4, px: 3}}>
        <Typography sx={{fontSize: 20, fontWeight: 600}}>Guide not found</Typography>
      </Box>
    );
  }

  const previewOutput = previewMode === 'draft' ? draftOutput : publishedOutput;
  const isPublished = !!publishedVersion?.publishedAt;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{maxWidth: 1200, margin: '0 auto', padding: '0 24px 64px'}}>
      {/* ================================================================
          Sticky header
          ================================================================ */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: '#F8FAFC',
          borderBottom: '1px solid #E5E7EB',
          mx: -3,
          px: 3,
          py: 2,
        }}
      >
        {/* Source group breadcrumb */}
        {sourceGroup && (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 1}}>
            <FolderIcon sx={{fontSize: 16, color: colors.blue[500]}} />
            <Typography
              sx={{
                fontSize: 13,
                color: colors.blue[600],
                cursor: 'pointer',
                '&:hover': {textDecoration: 'underline'},
              }}
              onClick={() => navigate(`/org/groups/${sourceGroupId}`)}
            >
              {sourceGroup.name}
            </Typography>
            <Typography sx={{fontSize: 13, color: '#9CA3AF', mx: 0.5}}>/</Typography>
            <Typography sx={{fontSize: 13, color: '#6B7280'}}>Edit Guide</Typography>
          </Box>
        )}

        {/* Out-of-date banner */}
        {isOutOfDate && sourceGroup && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon sx={{fontSize: 20}} />}
            sx={{mb: 1.5, borderRadius: '8px'}}
            action={
              <Button
                size="small"
                variant="outlined"
                onClick={handleRegenerateFromGroup}
                disabled={regeneratingFromGroup}
                sx={{
                  textTransform: 'none',
                  borderColor: colors.amber[200],
                  color: colors.amber[700],
                  '&:hover': {
                    borderColor: colors.amber[400],
                    backgroundColor: colors.amber[50],
                  },
                }}
              >
                {regeneratingFromGroup ? <CircularProgress size={14} sx={{mr: 0.5}} /> : null}
                Refresh from sources
              </Button>
            }
          >
            Source documents changed since this guide was generated.
          </Alert>
        )}

        {/* Page title + actions */}
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
          <Box>
            <Typography sx={{fontSize: 24, fontWeight: 700, lineHeight: 1.2}}>
              {editData.title || 'Untitled Guide'}
            </Typography>
            <Typography sx={{fontSize: 13, color: '#6B7280', mt: 0.5}}>
              {isPublished ? 'Published' : 'Draft only'}
              {editData.publishedAt && ` • Updated ${formatRelativeTime(editData.publishedAt)}`}
            </Typography>
          </Box>

          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            {isPublished && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon sx={{fontSize: 14}} />}
                onClick={() => window.open(`/#${guidePublicUrl}`, '_blank')}
                sx={{textTransform: 'none', fontWeight: 500}}
              >
                View published guide
              </Button>
            )}

            <Tooltip title={saveDraftDisabledReason || ''}>
              <span>
                <Button
                  variant="outlined"
                  onClick={handleSaveDraft}
                  disabled={!!saveDraftDisabledReason || saving}
                  size="small"
                  sx={{textTransform: 'none'}}
                >
                  {saving ? <CircularProgress size={14} sx={{mr: 0.5}} /> : null}
                  Save draft
                </Button>
              </span>
            </Tooltip>

            <Tooltip title={publishDisabledReason || ''}>
              <span>
                <Button
                  variant="contained"
                  onClick={() => setPublishDialogOpen(true)}
                  disabled={!!publishDisabledReason || publishing}
                  size="small"
                  sx={{textTransform: 'none'}}
                >
                  {publishing ? <CircularProgress size={14} sx={{mr: 0.5}} /> : null}
                  Publish
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Main split layout */}
      <Box sx={{display: 'flex', gap: 3, mt: 2}}>
        {/* LEFT — Prompt + controls */}
        <Box sx={{flex: 1, minWidth: 0}}>
          {/* Guide branding */}
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              p: 2.5,
              mb: 2,
            }}
          >
            <Typography sx={{fontSize: 14, fontWeight: 600, mb: 1}}>Guide branding</Typography>
            <Typography sx={{fontSize: 12, color: '#6B7280', mb: 1.5}}>
              Choose how this guide inherits organization branding.
            </Typography>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={themeMode}
              onChange={(_event, nextMode) => {
                if (!nextMode) return;
                setThemeMode(nextMode);
                if (nextMode === 'ORG_DEFAULT') {
                  setThemeProfileId('');
                }
                setThemeDirty(true);
              }}
              sx={{mb: 1.5}}
            >
              <ToggleButton value="ORG_DEFAULT" sx={{textTransform: 'none', fontSize: 12}}>
                Org default
              </ToggleButton>
              <ToggleButton value="IMPORT_PROFILE" sx={{textTransform: 'none', fontSize: 12}}>
                Import profile
              </ToggleButton>
              <ToggleButton value="GUIDE_OVERRIDE" sx={{textTransform: 'none', fontSize: 12}}>
                Guide override
              </ToggleButton>
            </ToggleButtonGroup>

            {themeMode === 'IMPORT_PROFILE' && (
              <TextField
                select
                fullWidth
                label="Brand profile"
                size="small"
                value={themeProfileId}
                onChange={event => {
                  setThemeProfileId(event.target.value);
                  setThemeDirty(true);
                }}
                sx={{mb: 1.5}}
                helperText={brandProfiles.length === 0 ? 'No approved profiles available yet' : undefined}
              >
                <MenuItem value="">
                  <em>Select a profile</em>
                </MenuItem>
                {brandProfiles.map((profile: any) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.name}
                    {profile.isDefault ? ' (default)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {(themeMode === 'GUIDE_OVERRIDE' || themeMode === 'IMPORT_PROFILE') && (
              <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1.5}}>
                <TextField
                  label="Organization display name"
                  size="small"
                  value={themeOverrides.organizationDisplayName}
                  onChange={event => {
                    setThemeOverrides(prev => ({...prev, organizationDisplayName: event.target.value}));
                    setThemeDirty(true);
                  }}
                />
                <TextField
                  label="Brand logo URL"
                  size="small"
                  value={themeOverrides.logoUrl}
                  onChange={event => {
                    setThemeOverrides(prev => ({...prev, logoUrl: event.target.value}));
                    setThemeDirty(true);
                  }}
                />
                <TextField
                  label="Header logo URL"
                  size="small"
                  value={themeOverrides.headerLogoUrl}
                  onChange={event => {
                    setThemeOverrides(prev => ({...prev, headerLogoUrl: event.target.value}));
                    setThemeDirty(true);
                  }}
                />
                <TextField
                  label="Primary color"
                  size="small"
                  value={themeOverrides.primaryColor}
                  onChange={event => {
                    setThemeOverrides(prev => ({...prev, primaryColor: event.target.value}));
                    setThemeDirty(true);
                  }}
                />
                <TextField
                  label="Secondary color"
                  size="small"
                  value={themeOverrides.secondaryColor}
                  onChange={event => {
                    setThemeOverrides(prev => ({...prev, secondaryColor: event.target.value}));
                    setThemeDirty(true);
                  }}
                />
                <TextField
                  select
                  label="Tone"
                  size="small"
                  value={themeOverrides.tone}
                  onChange={event => {
                    setThemeOverrides(prev => ({
                      ...prev,
                      tone: event.target.value as 'FORMAL' | 'FRIENDLY' | 'NEUTRAL',
                    }));
                    setThemeDirty(true);
                  }}
                >
                  <MenuItem value="FORMAL">Formal</MenuItem>
                  <MenuItem value="FRIENDLY">Friendly</MenuItem>
                  <MenuItem value="NEUTRAL">Neutral</MenuItem>
                </TextField>
              </Box>
            )}

            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid #E5E7EB',
                bgcolor: '#F9FAFB',
                mb: 1.5,
              }}
            >
              <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B7280', mb: 0.75}}>
                Current resolved theme
              </Typography>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    border: '1px solid #D1D5DB',
                    bgcolor: editData?.resolvedTheme?.primaryColor || '#2563eb',
                  }}
                />
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    border: '1px solid #D1D5DB',
                    bgcolor: editData?.resolvedTheme?.secondaryColor || '#dbeafe',
                  }}
                />
                <Typography sx={{fontSize: 13, color: '#4B5563'}}>
                  {(editData?.resolvedTheme?.organizationDisplayName || 'Planapus')} · {editData?.resolvedTheme?.source || 'PLANAPUS_DEFAULT'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSaveTheme}
                disabled={!themeDirty || savingTheme}
                sx={{textTransform: 'none'}}
              >
                {savingTheme ? <CircularProgress size={14} sx={{mr: 0.5}} /> : null}
                Save branding
              </Button>
            </Box>
          </Box>

          {/* AI Prompt (collapsible) */}
          <Accordion
            defaultExpanded
            disableGutters
            sx={{
              bgcolor: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: '12px !important',
              mb: 2,
              '&:before': {display: 'none'},
              boxShadow: 'none',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{px: 2.5}}>
              <Typography sx={{fontSize: 14, fontWeight: 600}}>AI Prompt</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{px: 2.5, pt: 0, pb: 2.5}}>
              <Typography sx={{fontSize: 13, color: '#6B7280', mb: 1}}>
                Describe the guide you want to generate. Edit and regenerate to refine.
              </Typography>
              <TextField
                multiline
                minRows={4}
                maxRows={14}
                fullWidth
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Describe the guide you want to generate..."
                sx={{mb: 1.5}}
              />
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Tooltip title={regenerateDisabledReason || ''}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={draftVersion ? handleRegenerate : handleSaveDraft}
                      disabled={!!regenerateDisabledReason}
                      sx={{textTransform: 'none'}}
                    >
                      {isGenerating ? <CircularProgress size={14} sx={{mr: 0.5}} /> : null}
                      {isGenerating ? 'Generating...' : draftVersion ? 'Regenerate from prompt' : 'Generate'}
                    </Button>
                  </span>
                </Tooltip>
                {regenerateDisabledReason && !isGenerating && (
                  <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>{regenerateDisabledReason}</Typography>
                )}
              </Box>

              {draftVersion?.generationError && (
                <Alert severity="error" sx={{mt: 1.5}} variant="outlined">
                  {draftVersion.generationError}
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Attachments */}
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              p: 2.5,
              mb: 2,
            }}
          >
            <Typography sx={{fontSize: 14, fontWeight: 600, mb: 1.5}}>Attachments</Typography>

            {fileUpload.attachments.map(att => (
              <AttachmentPreview
                key={att.id}
                attachment={att}
                onRemove={fileUpload.removeAttachment}
              />
            ))}

            {(() => {
              const inheritedFiles = draftVersion?.generationFiles?.length > 0
                ? draftVersion.generationFiles
                : publishedVersion?.generationFiles?.length > 0
                  ? publishedVersion.generationFiles
                  : [];
              const label = draftVersion?.generationFiles?.length > 0
                ? 'Source documents'
                : 'Inherited from published version';
              if (inheritedFiles.length > 0 && fileUpload.attachments.length === 0) {
                return (
                  <Box sx={{mb: 1}}>
                    <Typography sx={{fontSize: 12, color: '#9CA3AF', mb: 0.75}}>
                      {label}
                    </Typography>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                      {inheritedFiles.map((file: {id: string; fileName: string; mimeType: string; sizeBytes: number}) => (
                        <Box
                          key={file.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 2,
                            border: '1px solid #E5E7EB',
                            bgcolor: '#F9FAFB',
                            maxWidth: 220,
                          }}
                        >
                          <InheritedFileIcon mimeType={file.mimeType} />
                          <Box sx={{minWidth: 0}}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {file.fileName}
                            </Typography>
                            <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
                              {formatFileSize(file.sizeBytes)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              }
              return null;
            })()}

            {fileUpload.attachments.length === 0 && !(draftVersion?.generationFiles?.length > 0) && !(publishedVersion?.generationFiles?.length > 0) && (
              <Typography sx={{fontSize: 13, color: '#9CA3AF', mb: 1}}>
                No attachments. Add reference files for the AI to use.
              </Typography>
            )}

            <Button variant="outlined" size="small" component="label" sx={{textTransform: 'none'}}>
              Add files
              <input
                type="file"
                hidden
                multiple
                onChange={e => {
                  if (e.target.files) fileUpload.addFiles(e.target.files);
                }}
              />
            </Button>
          </Box>
        </Box>

        {/* RIGHT — Preview pane */}
        <Box
          sx={{
            width: 400,
            flexShrink: 0,
            position: 'sticky',
            top: 100,
          }}
        >
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            {/* Preview header + toggle */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <ToggleButtonGroup
                value={previewMode}
                exclusive
                onChange={(_, val) => val && setPreviewMode(val)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.5,
                    textTransform: 'none',
                    fontSize: 13,
                    fontWeight: 500,
                  },
                }}
              >
                <ToggleButton value="draft">Draft preview</ToggleButton>
                <ToggleButton value="published" disabled={!publishedOutput}>
                  Published
                </ToggleButton>
              </ToggleButtonGroup>

              {isPublished && (
                <Tooltip title="Open full preview">
                  <IconButton
                    size="small"
                    onClick={() => window.open(`/#${guidePublicUrl}`, '_blank')}
                  >
                    <OpenInNewIcon sx={{fontSize: 16}} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Preview body */}
            <Box sx={{p: 2.5, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto'}}>
              {previewOutput ? (
                <GuideContentPreview output={previewOutput} />
              ) : previewMode === 'draft' ? (
                <Box sx={{textAlign: 'center', py: 4}}>
                  <Typography sx={{fontSize: 14, color: '#9CA3AF'}}>
                    No draft content yet.
                  </Typography>
                  <Typography sx={{fontSize: 13, color: '#9CA3AF', mt: 0.5}}>
                    Add a prompt and generate to see a preview.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{textAlign: 'center', py: 4}}>
                  <Typography sx={{fontSize: 14, color: '#9CA3AF'}}>
                    No published version yet.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Open full preview link */}
            {isPublished && previewMode === 'published' && (
              <Box sx={{px: 2, py: 1.5, borderTop: '1px solid #E5E7EB'}}>
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon sx={{fontSize: 14}} />}
                  onClick={() => window.open(`/#${guidePublicUrl}`, '_blank')}
                  sx={{textTransform: 'none', fontSize: 13, color: '#6B7280'}}
                >
                  Open full preview
                </Button>
              </Box>
            )}
          </Box>

          {/* Source documents card */}
          {sourceGroup && (
            <Box
              sx={{
                bgcolor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                overflow: 'hidden',
                mt: 2,
              }}
            >
              <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <FolderIcon sx={{fontSize: 18, color: colors.blue[500]}} />
                  <Typography sx={{fontSize: 13, fontWeight: 600}}>Source Documents</Typography>
                </Box>
                {isOutOfDate && (
                  <Chip
                    label="Out of date"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      bgcolor: colors.amber[50],
                      color: colors.amber[700],
                      border: `1px solid ${colors.amber[200]}`,
                    }}
                  />
                )}
              </Box>
              <Box sx={{p: 2}}>
                <Typography sx={{fontSize: 13, color: '#6B7280', mb: 1}}>
                  Generated from <strong>{sourceGroup.name}</strong>
                  {sourceGroup.members && ` (${sourceGroup.members.length} source${sourceGroup.members.length === 1 ? '' : 's'})`}
                </Typography>
                {editData?.generatedAt && (
                  <Typography sx={{fontSize: 12, color: '#9CA3AF', mb: 1.5}}>
                    Last generated {formatRelativeTime(editData.generatedAt)}
                  </Typography>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FolderIcon sx={{fontSize: 14}} />}
                  onClick={() => navigate(`/org/groups/${sourceGroupId}`)}
                  sx={{textTransform: 'none', fontSize: 12}}
                >
                  View source group
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* ================================================================
          Publish confirmation dialog
          ================================================================ */}
      <Dialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{fontWeight: 600}}>Publish changes to this guide?</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize: 14, color: '#6B7280'}}>
            {isPublished
              ? 'This will replace the currently published guide with the draft version.'
              : 'This will make the guide publicly accessible.'}
          </Typography>
          {draftOutput && (
            <Box sx={{mt: 2, p: 1.5, bgcolor: '#F9FAFB', borderRadius: '8px'}}>
              <Typography sx={{fontSize: 13, fontWeight: 600}}>{draftOutput.title}</Typography>
              <Typography sx={{fontSize: 12, color: '#6B7280', mt: 0.25}}>
                {draftOutput.steps?.length || 0} steps
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)} sx={{textTransform: 'none'}}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={publishing}
            sx={{textTransform: 'none'}}
          >
            {publishing ? <CircularProgress size={14} sx={{mr: 0.5}} /> : null}
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================
          Publish success dialog
          ================================================================ */}
      <Dialog
        open={publishSuccess}
        onClose={() => setPublishSuccess(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{fontWeight: 600}}>Published!</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize: 14, color: '#6B7280', mb: 2}}>
            Your guide is now live and accessible to the public.
          </Typography>
          <Box sx={{display: 'flex', gap: 1}}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewIcon sx={{fontSize: 14}} />}
              onClick={() => window.open(`/#${guidePublicUrl}`, '_blank')}
              sx={{textTransform: 'none'}}
            >
              View public page
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon sx={{fontSize: 14}} />}
              onClick={handleCopyLink}
              sx={{textTransform: 'none'}}
            >
              Copy link
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishSuccess(false)} sx={{textTransform: 'none'}}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          onClose={() => setSnackbar({...snackbar, open: false})}
          severity={snackbar.severity}
          sx={{width: '100%'}}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
